import type {
  NotificationChannel,
  NotificationEvent,
} from '@/lib/services/notification-events/notification-events.types'
import type { NotificationRecipient } from '@/lib/services/notification-recipients/notification-recipients.types'

export type NotificationEventRecipientMap = Record<number, number[]>
type NotificationEventRecipientInputMap = Record<number, number[] | null | undefined>
type NotificationChannelSupport = Partial<
  Pick<NotificationEvent, 'channel_support' | 'supported_channels'>
>

export function normalizeRecipientIds(
  recipientIds: number[] = [],
  allowedRecipientIds: ReadonlySet<number> | null = null
): number[] {
  const filteredRecipientIds = allowedRecipientIds
    ? recipientIds.filter((recipientId) => allowedRecipientIds.has(Number(recipientId)))
    : recipientIds

  return [...new Set(filteredRecipientIds)].sort((left, right) => left - right)
}

export function normalizeEventRecipientMap(
  eventRecipientIds: NotificationEventRecipientInputMap = {},
  allowedRecipientIds: ReadonlySet<number> | null = null
): NotificationEventRecipientMap {
  return Object.fromEntries(
    Object.entries(eventRecipientIds).map(([eventId, recipientIds]) => [
      eventId,
      normalizeRecipientIds(recipientIds ?? [], allowedRecipientIds),
    ])
  )
}

export function buildNotificationEventFormValues(
  notificationEvents: Pick<NotificationEvent, 'id' | 'recipient_ids'>[],
  allowedRecipientIds: ReadonlySet<number> | null = null
): { eventRecipientIds: NotificationEventRecipientMap } {
  return {
    eventRecipientIds: normalizeEventRecipientMap(
      Object.fromEntries(
        notificationEvents.map((event) => [
          event.id,
          normalizeRecipientIds(event.recipient_ids ?? [], allowedRecipientIds),
        ])
      ),
      allowedRecipientIds
    ),
  }
}

export function notificationEventSupportsChannel(
  event: NotificationChannelSupport,
  channel: NotificationChannel
): boolean {
  if (event.channel_support) {
    return Boolean(event.channel_support[channel])
  }

  return (event.supported_channels ?? []).includes(channel)
}

export function getEnabledNotificationRecipients<T extends Pick<NotificationRecipient, 'enabled'>>(
  notificationRecipients: T[] = []
): T[] {
  return notificationRecipients.filter((recipient) => recipient.enabled)
}

export function getEnabledNotificationRecipientIds(
  notificationRecipients: Pick<NotificationRecipient, 'id' | 'enabled'>[] = []
): Set<number> {
  return new Set(
    getEnabledNotificationRecipients(notificationRecipients).map((recipient) =>
      Number(recipient.id)
    )
  )
}

export function getNotificationRecipientsByChannel<
  T extends Pick<NotificationRecipient, 'channel'>,
>(
  notificationRecipients: T[] = [],
  channelOrder: NotificationChannel[] = []
): Partial<Record<NotificationChannel, T[]>> {
  return channelOrder.reduce<Partial<Record<NotificationChannel, T[]>>>((acc, channel) => {
    acc[channel] = notificationRecipients.filter((recipient) => recipient.channel === channel)
    return acc
  }, {})
}

export function getEffectiveNotificationRecipientsById<
  T extends Pick<NotificationRecipient, 'id' | 'enabled' | 'verified_at'>,
>(notificationRecipients: T[] = []): Map<number, T> {
  return new Map(
    notificationRecipients
      .filter((recipient) => recipient?.enabled === true && recipient?.verified_at != null)
      .map((recipient) => [Number(recipient.id), recipient])
  )
}

export function hasEffectiveNotificationRecipientForEvent(
  event: NotificationChannelSupport,
  recipientIds: number[] = [],
  recipientsById: ReadonlyMap<number, Pick<NotificationRecipient, 'channel'>>
): boolean {
  return recipientIds.some((recipientId) => {
    const recipient = recipientsById.get(Number(recipientId))

    return recipient && notificationEventSupportsChannel(event, recipient.channel)
  })
}

export function hasEffectiveNotificationRecipient(
  notificationEvents: (NotificationChannelSupport &
    Pick<NotificationEvent, 'recipient_ids'>)[] = [],
  notificationRecipients: Pick<
    NotificationRecipient,
    'id' | 'channel' | 'enabled' | 'verified_at'
  >[] = []
): boolean {
  const verifiedEnabledRecipientsById =
    getEffectiveNotificationRecipientsById(notificationRecipients)

  return notificationEvents.some((event) =>
    hasEffectiveNotificationRecipientForEvent(
      event,
      event.recipient_ids ?? [],
      verifiedEnabledRecipientsById
    )
  )
}

export function getChangedNotificationEvents<T extends Pick<NotificationEvent, 'id'>>(
  notificationEvents: T[],
  baselineEventRecipientIds: NotificationEventRecipientInputMap,
  nextEventRecipientIds: NotificationEventRecipientInputMap
): T[] {
  return notificationEvents.filter((event) => {
    const currentRecipientIds = normalizeRecipientIds(baselineEventRecipientIds[event.id] ?? [])
    const nextRecipientIds = nextEventRecipientIds[event.id] ?? []

    if (currentRecipientIds.length !== nextRecipientIds.length) {
      return true
    }

    return currentRecipientIds.some((recipientId, index) => recipientId !== nextRecipientIds[index])
  })
}
